import uniqBy from 'lodash-es/uniqBy';
import { fetchKeywords, addComparingGames, deleteComparingGames } from '@/pages/intelligence/services/compare';
import {
  fetchGamesByKeyword,
  fetchSimilarGames,
  fetchRegionsCountries,
  fetchPCConsoleCompareTableResults,
  fetchPCConsoleCompareTrendsResults,
  fetchDownloadTaskId,
} from '@/pages/intelligence/services/pcConsoleCompare';
import {
  getReleaseTimeByDevices,
  getEarlistedReleaseTime,
  getDateTimeByRelativeDateTime,
  handleRegions,
  getRegionsReqParamsByRegionSelectorValue,
  getSpecialArrayValue,
} from '../utils';
import { getDataSourcesAndMetricsForReq, analyseDataSourceMetricKey } from '../utils/dataSourcesMetrics';
import { ComposedDateMode } from '@/pages/intelligence/components/ComposedDatePicker/enums';
import { ALL_DEVICES, ALL_SEGMENTS } from '../constants';

import type { IDateType, IDateValue, ICommonActionRes } from '@/pages/intelligence/types/common';
import type {
  IRegionValueDataOfRegionSelector,
  IRegion,
  IPCConsoleGamesCompareTableResult,
  IPCConsoleGamesCompareTrendsResult,
  IPCConsoleGame,
} from '@/pages/intelligence/types/pcConsoleCompare';
import type { IComposedDateData } from '@/pages/intelligence/components/ComposedDatePicker/types';

export async function fetchKeywordsPlus(keyword: any): Promise<string[]> {
  const params = {
    entity_type: 'pc,console',
    keyword,
    system_type: 'pc',
    complement: '1',
  };
  let res: any;
  let keywords: string[] = [];
  try {
    res = await fetchKeywords(params);
    keywords = res?.data?.result;
    keywords = Array.isArray(keywords) ? keywords : [];
  } catch (exception) {
    keywords = [];
  }
  return keywords;
}

export async function fetchGamesByKeywordPlus(keyword: string): Promise<IPCConsoleGame[]> {
  const params = {
    entity_type: 'pc,console',
    keyword,
    system_type: 'pc',
  };
  let games: IPCConsoleGame[] = [];
  try {
    const gamesRes = await fetchGamesByKeyword(params);
    for (const gameRes of gamesRes) {
      const entityType = gameRes.entity_type;
      const gameId = gameRes?.game_id;
      const gameName = gameRes?.entity_name;
      if (!entityType || !gameId || !gameName) continue;
      games.push({
        ...gameRes,
        entityType,
        gameId,
        gameName,
        cover: gameRes.cover || '',
        pcCover: gameRes.cover || '',
        consoleCover: gameRes.cover || '',
        publisher: gameRes.publisher || '',
        releaseTime: gameRes.release_time || '',
        pcReleaseTime: gameRes.pc_release_time,
        consoleReleaseTime: gameRes.console_release_time,
      });
    }
  } catch (exception) {
    games = [];
  }
  return games;
}

export async function fetchSimilarGamesPlus(
  entities: {
    entityType: string;
    gameId: string;
  }[],
): Promise<IPCConsoleGame[]> {
  const gamesIdsData = entities?.map((entity: any) => ({
    entity_type: entity?.entityType,
    game_id: entity?.gameId,
  }));
  const params = {
    entity_type: 'pc,console',
    game_ids: gamesIdsData,
  };
  let games: IPCConsoleGame[] = [];
  try {
    const gamesRes = await fetchSimilarGames(params);
    for (const gameRes of gamesRes) {
      const entityType = gameRes.entity_type;
      const gameId = gameRes?.app_id;
      const gameName = gameRes?.name;
      if (!entityType || !gameId || !gameName) continue;
      games.push({
        ...gameRes,
        entityType,
        gameId,
        gameName,
        cover: gameRes?.cover || '',
        pccCover: gameRes.cover || '',
        consoleCover: gameRes.cover || '',
        publisher: gameRes.publisher || '',
        releaseTime: gameRes.time || '',
        pcReleaseTime: gameRes.pc_release_time,
        consoleReleaseTime: gameRes.console_release_time,
      });
    }
  } catch (exception) {
    games = [];
  }

  return games;
}

export async function fetchMAURegionsCountriesPlus(): Promise<IRegion[]> {
  let regionsCountries: IRegion[] = [];
  try {
    const res = await fetchRegionsCountries({
      filter_type: 'common',
      type: [{ tabName: 'mobile' }],
      item: [{ tabName: '' }],
    });
    // @ts-ignore
    regionsCountries = handleRegions(res);
  } catch (exception) {
    regionsCountries = [];
  }
  return regionsCountries;
}

export async function fetchDigitalRevenueRegionsCountriesPlus(): Promise<IRegion[]> {
  let regionsCountries: IRegion[] = [];
  try {
    const res = await fetchRegionsCountries({
      filter_type: 'digital_revenue',
      type: [{ tabName: 'mobile' }],
      item: [{ tabName: '' }],
    });
    // @ts-ignore
    regionsCountries = handleRegions(res);
  } catch (exception) {
    regionsCountries = [];
  }
  return regionsCountries;
}

export async function fetchPCConsoleCompareTableResultsPlus(
  params: {
    entityTypes?: string[];
    games: IPCConsoleGame[];
    metrics: string[];
    devices?: string[];
    segments?: string[];
    regionData?: IRegionValueDataOfRegionSelector;
    isShowTop3Countries?: boolean;
  },
  option?: { devices?: string[] },
): Promise<{
  compare_list: IPCConsoleGamesCompareTableResult[];
}> {
  const allDevices = option?.devices || [];
  const { entityTypes, games, metrics, devices, segments, regionData, isShowTop3Countries = false } = params || {};
  const gamesTemp = games?.map((game) => ({
    entity_type: game?.entityType || '',
    game_id: game?.gameId || '',
    start_date: '',
    end_date: '',
  }));
  const { dataSourcesKeys, metricsKeys } = getDataSourcesAndMetricsForReq(metrics);
  const { regionType, regions } = getRegionsReqParamsByRegionSelectorValue(regionData);
  const paramsPlus = {
    entity_type: entityTypes && entityTypes?.length > 0 ? entityTypes : ['pc', 'console'],
    game_ids: Array.isArray(gamesTemp) ? gamesTemp : [],
    data_sources: Array.isArray(dataSourcesKeys) ? dataSourcesKeys : [],
    metrics: Array.isArray(metricsKeys) ? metricsKeys : [],
    // device: Array.isArray(devices) ? devices : [],
    // segment: Array.isArray(segments) ? segments : [],
    device: getSpecialArrayValue(devices, allDevices),
    segment: getSpecialArrayValue(segments, ALL_SEGMENTS),
    region_type: regionType || 'region',
    regions: regions?.length ? regions : ['global'],
    // 展示top 3 countries 就传2 否则传0
    is_country: isShowTop3Countries ? 2 : 0,
  };
  let res: any;
  let compareResults: IPCConsoleGamesCompareTableResult[] = [];
  try {
    res = await fetchPCConsoleCompareTableResults(paramsPlus);
    compareResults = res?.compare_list;
    compareResults = Array.isArray(compareResults) ? compareResults : [];
    compareResults = uniqBy(compareResults, 'game_id');
    compareResults = compareResults?.map((item) => ({
      ...item,
      entityType: item?.entity_type,
      gameId: item?.game_id,
      pcGameId: item?.pc_id,
      consoleGameId: item?.console_id,
      gameName: item?.name,
      cover: item?.cover || item?.console_cover,
      pcCover: item?.cover,
      consoleCover: item?.console_cover,
      publisher: item?.publisher,
      releaseTime: item?.release_time,
      pcReleaseTime: item?.pc_release_time,
      consoleReleaseTime: item?.console_release_time,
    }));
  } catch (exception) {
    compareResults = [];
  }
  return {
    ...res,
    compare_list: compareResults,
  };
}

function getGamesData(params: {
  isRelativeDate: boolean;
  games: IPCConsoleGame[];
  devices?: string[];
  dateType: IDateType;
  dateValue: IDateValue;
  getReleaseTime: (game: IPCConsoleGame, devices?: string[]) => string;
}) {
  const { isRelativeDate, games, devices, dateType, dateValue, getReleaseTime } = params || {};
  if (!Array.isArray(games)) return [];
  return games.map((game) => {
    let startDate = '';
    let endDate = '';
    // 相对时间时，需要根据页面选择的相对时间、每个游戏的发布时间 计算出 每个游戏的 开始时间、结束时间
    if (isRelativeDate) {
      const releaseTime = getReleaseTime?.(game, devices);
      const relativeDateData = getDateTimeByRelativeDateTime({
        dateType,
        releaseTime,
        dateValue,
      });
      startDate = relativeDateData?.startDate;
      endDate = relativeDateData?.endDate;
    }
    return {
      entity_type: game?.entityType || '',
      game_id: game?.gameId || '',
      start_date: startDate,
      end_date: endDate,
    };
  });
}

export async function fetchPCConsoleCompareTrendsResultsPlus(
  params: {
    entityTypes?: string[];
    games: IPCConsoleGame[];
    metrics: string[];
    devices?: string[];
    segments?: string[];
    regionData?: IRegionValueDataOfRegionSelector;
    composedDateData: IComposedDateData;
  },
  option?: { devices?: string[] },
): Promise<{ trends: IPCConsoleGamesCompareTrendsResult[]; period: string; startDate: string; endDate: string }> {
  const allDevices = option?.devices || [];
  const { entityTypes, metrics, games, devices, segments, regionData, composedDateData } = params || {};
  const { mode, dateType, dateValue } = composedDateData || {};
  const isRelativeDate = mode === ComposedDateMode.Relative;
  const period = dateType === 'date' ? 'day' : dateType;
  const [startDate, endDate] = dateValue || [];
  const gamesData = getGamesData({
    isRelativeDate,
    games,
    devices,
    dateType,
    dateValue,
    getReleaseTime: getReleaseTimeByDevices,
  });
  const { dataSourcesKeys, metricsKeys } = getDataSourcesAndMetricsForReq(metrics);
  const { regionType, regions } = getRegionsReqParamsByRegionSelectorValue(regionData);
  // launch_date字段，绝对时间传0，相对时间 传 开始那个，比如M10 ~ M20  就传10
  const launchDate = (() => {
    if (!isRelativeDate) return 0;
    const launchDateNum = Number.parseInt(dateValue?.[0], 10);
    return Number.isNaN(launchDateNum) ? 0 : launchDateNum;
  })();
  const paramsPlus = {
    entity_type: entityTypes && entityTypes?.length > 0 ? entityTypes : ['pc', 'console'],
    game_ids: gamesData,
    data_sources: Array.isArray(dataSourcesKeys) ? dataSourcesKeys : [],
    metrics: Array.isArray(metricsKeys) ? metricsKeys : [],
    // device: Array.isArray(devices) ? devices : [],
    // segment: Array.isArray(segments) ? segments : [],
    device: getSpecialArrayValue(devices, allDevices),
    segment: getSpecialArrayValue(segments, ALL_SEGMENTS),
    region_type: regionType || 'region',
    regions: regions?.length ? regions : ['global'],
    launch_date: launchDate,
    period: period || 'day',
    start_date: isRelativeDate ? '' : startDate || '',
    end_date: isRelativeDate ? '' : endDate || '',
  };
  let res: any;
  let resData: any;
  let resReq: any;
  let compareResults: IPCConsoleGamesCompareTrendsResult[] = [];
  let resPeriod = '';
  let resStartDate = '';
  let resEndDate = '';
  try {
    res = await fetchPCConsoleCompareTrendsResults(paramsPlus);
    res = res?.data;
    resData = res?.data;
    resReq = res?.req;
    compareResults = resData?.trends;
    compareResults = Array.isArray(compareResults) ? compareResults : [];
    compareResults = uniqBy(compareResults, 'game_id');
    resPeriod = resReq?.period || 'day';
    resStartDate = resReq?.start_date || '';
    resEndDate = resReq?.end_date || '';
  } catch (exception) {
    compareResults = [];
    resPeriod = 'day';
    resStartDate = '';
    resEndDate = '';
  }
  return {
    trends: compareResults,
    period: resPeriod,
    startDate: resStartDate,
    endDate: resEndDate,
  };
}

// 添加对比游戏
export async function addComparingGamesPlus(games: IPCConsoleGame[]): Promise<ICommonActionRes> {
  let flag: boolean;
  try {
    let gamesTemp: any = games?.map((game, index) => ({
      entity_type: game?.entityType,
      game_id: game?.gameId,
      sequence: index + 1,
    }));
    gamesTemp = Array.isArray(gamesTemp) ? gamesTemp : [];
    gamesTemp = uniqBy(gamesTemp, 'game_id');
    await addComparingGames({
      entity_type: 'pc,console',
      game_list: gamesTemp,
    });
    flag = true;
  } catch (exception) {
    flag = false;
  }
  return {
    status: flag,
  };
}

// 删除PCConsole对比中的对比游戏（不是删除游戏，目前沿用mobile对比 删除对比游戏的接口）
export async function deleteComparingGamesPlus(ids: string[]): Promise<ICommonActionRes> {
  let flag: boolean;
  try {
    const idsTemp = Array.from(new Set(ids));
    await deleteComparingGames({ id: idsTemp });
    flag = true;
  } catch (exception) {
    flag = false;
  }
  return {
    status: flag,
  };
}

// 下载
export async function fetchDownloadTaskIdPlus(params: {
  pageName: string;
  games: IPCConsoleGame[];
  devices?: string[];
  regionData?: IRegionValueDataOfRegionSelector;
  composedDateData: IComposedDateData;
  downloadDateValue: IDateValue;
  downloadConfig: any;
}): Promise<any> {
  const { pageName, games, devices, regionData, composedDateData, downloadDateValue, downloadConfig } = params || {};
  const { regionType, regions } = getRegionsReqParamsByRegionSelectorValue(regionData);
  const { mode, dateType, dateValue } = composedDateData || {};
  const isRelativeDate = mode === ComposedDateMode.Relative;
  const gamesData = getGamesData({
    isRelativeDate,
    games,
    devices,
    dateType,
    dateValue,
    getReleaseTime: getEarlistedReleaseTime,
  });
  const downloadMetrics = downloadConfig?.metrics || [];
  /* 组装 source_metrics的参数 */
  const sourcesMetrics = (() => {
    const sourcesMetricsTemp: { [key: string]: string[] } = {};
    const orderMetricsTemp: any[] = [];
    for (const downloadMetric of downloadMetrics) {
      const { dataSourceKey, metricKey } = analyseDataSourceMetricKey(downloadMetric);
      if (!Array.isArray(sourcesMetricsTemp[dataSourceKey])) {
        sourcesMetricsTemp[dataSourceKey] = [];
      }
      if(metricKey==='arppu'){
        sourcesMetricsTemp[dataSourceKey].push('digitalARPPU');
      }else if(metricKey==='revenue'){
        sourcesMetricsTemp[dataSourceKey].push('digitalRevenue');
      }else{
        sourcesMetricsTemp[dataSourceKey].push(metricKey);
      }
      orderMetricsTemp.push({
        dataSource: dataSourceKey,
        metric: metricKey,
      });
    }
    return sourcesMetricsTemp;
  })();
  /* 组装 source_metrics的参数 */
  const dateTypesMapping: {
    [key in IDateType]?: string;
  } = {
    date: 'daily',
    week: 'weekly',
    month: 'monthly',
  };
  const paramsPlus = {
    entity_type: 'pc,console',
    region_type: regionType || 'market',
    regions: regions?.length ? regions : ['global'],
    compare_is_launch_date: isRelativeDate,
    compare_download_date: gamesData,
    source_metrics: sourcesMetrics,
    // 格式转换
    date_type: dateTypesMapping?.[dateType] || 'monthly',
    start_date: downloadDateValue?.[0] || '',
    end_date: downloadDateValue?.[1] || '',
    download_devices: ALL_DEVICES,
    source_download_device: {
      steamcharts: ['PC'],
      newzoo: ['All', 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S'],
      mscience: ['All', 'PC', 'PlayStation', 'Xbox', 'Nintendo'],
      npd: [
        'All',
        'PC',
        'PlayStation 4',
        'PlayStation 5',
        'Xbox One',
        'Xbox Series X|S',
        'Nintendo Wii U',
        'Nintendo Switch',
      ],
    },
    download_segments: ['All', ...ALL_SEGMENTS],
    /* 下载表单弹窗那部分 */
    release_date_show: downloadConfig?.release_date_show ? 1 : 0,
    company_show: downloadConfig?.company_show ? 1 : 0,
    genre_show: downloadConfig?.genre_show ? 1 : 0,
    ratio_show: downloadConfig?.ratio_show ? 1 : 0,
    downlaod_date_method: downloadConfig?.date_method,
    download_date_type: downloadConfig?.date_method === 'aggregated' ? [] : downloadConfig?.date_types,
    region_method: downloadConfig?.region_method,
    file_format: Number.parseInt(downloadConfig?.file_format, 10) || 0,
    // 没有提供自定义文件名的入口，固定传死
    file_name: `${'PC&Console Compare'}_${pageName}`,
    /* 下载表单弹窗那部分 */

    /* 接口需要，但是页面没有控制的入口，所以传固定值 */
    order_source: 'steamcharts',
    order_metrics: [
      {
        dataSource: 'steamcharts',
        metric: 'pcu',
      },
      {
        dataSource: 'steamcharts',
        metric: 'acu',
      },
      {
        dataSource: 'newzoo',
        metric: 'mau',
      },
      {
        dataSource: 'mscience',
        metric: 'digitalRevenue',
      },
      {
        dataSource: 'mscience',
        metric: 'units',
      },
      {
        dataSource: 'mscience',
        metric: 'ast',
      },
      {
        dataSource: 'mscience',
        metric: 'digitalARPPU',
      },
      {
        dataSource: 'npd',
        metric: 'revenue',
      },
    ],
    order_metric: 'pcu',
    order: 'desc',
    ratio_type: 0,
    /* 接口需要，但是页面没有控制的入口，所以传固定值 */

    /* 业务不需要的参数，不确定接口是否有必传校验，所以全部传固定值 */
    region_show: 0,
    edition_method: 'aggregated',
    select_top: 0,
    genre_source: 'iegg',
    genre_types: [],
    iegg_genre_req: [],
    company: [],
    start_release_time: '',
    end_release_time: '',
    search_by_name: '',
    data_method: '',
    segment: 'All',
    unified_ids: [],
    page: 1,
    page_size: 100,
    /* 业务不需要的参数，不确定接口是否有必传校验，所以全部传固定值 */
  };
  return fetchDownloadTaskId(paramsPlus);
}
